<template>
  <div class="relative" ref="rootRef">
    <button
      type="button"
      class="relative w-9 h-9 rounded-lg flex items-center justify-center text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
      :title="t('notifications.title')"
      @click="toggle"
    >
      <Bell class="w-5 h-5" />
      <span
        v-if="unreadCount"
        class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-kila-mint text-brand-navy text-[10px] font-bold flex items-center justify-center"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="panelRef"
        :style="panelStyle"
        class="fixed w-[360px] max-w-[calc(100vw-16px)] rounded-xl bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 shadow-xl z-50 overflow-hidden"
      >
        <div class="px-4 h-11 flex items-center gap-2 border-b border-secondary-200 dark:border-secondary-700">
          <span class="text-[13px] font-bold text-brand-navy dark:text-secondary-100">{{ t('notifications.title') }}</span>
          <span v-if="unreadCount" class="text-xs text-secondary-500">{{ t('notifications.unread', { count: unreadCount }) }}</span>
          <div class="flex-1"></div>
          <button
            v-if="unreadCount"
            type="button"
            class="text-xs font-semibold text-primary-700 dark:text-brand-mint hover:underline"
            @click="markAllAsRead"
          >
            {{ t('notifications.markAllRead') }}
          </button>
        </div>

        <!-- Activar push del navegador -->
        <div
          v-if="permission !== 'unsupported' && !isPushEnabled"
          class="px-4 py-2.5 flex items-center gap-2 bg-kila-mint-subtle border-b border-secondary-200 dark:border-secondary-700 text-xs text-brand-navy dark:text-secondary-100"
        >
          <BellRing class="w-4 h-4 text-primary-700 dark:text-brand-mint flex-shrink-0" />
          <span class="flex-1">{{ t('notifications.enableHint') }}</span>
          <button type="button" class="font-semibold text-primary-700 dark:text-brand-mint hover:underline whitespace-nowrap" @click="onEnablePush">
            {{ t('notifications.enable') }}
          </button>
        </div>

        <div class="max-h-[420px] overflow-y-auto">
          <div v-if="!notifications.length" class="px-4 py-10 text-center">
            <Bell class="w-10 h-10 text-secondary-300 dark:text-secondary-600 mx-auto mb-2" />
            <p class="text-sm text-secondary-500">{{ t('notifications.empty') }}</p>
          </div>
          <button
            v-for="item in notifications"
            :key="item.id"
            type="button"
            class="w-full px-4 py-3 flex items-start gap-3 text-left border-b border-secondary-100 dark:border-secondary-700/60 hover:bg-secondary-50 dark:hover:bg-secondary-700/60 transition-colors"
            :class="item.read ? '' : 'bg-kila-mint-subtle/60'"
            @click="openItem(item)"
          >
            <span
              class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              :class="item.type === 'email_associated' || item.type === 'email_mention' ? 'bg-primary-50 dark:bg-brand-mint/15 text-primary-700 dark:text-brand-mint' : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300'"
            >
              <Link2 v-if="item.type === 'email_associated'" class="w-4 h-4" />
              <AtSign v-else-if="item.type === 'email_mention'" class="w-4 h-4" />
              <Mail v-else class="w-4 h-4" />
            </span>
            <span class="flex-1 min-w-0">
              <span class="block text-[13px] truncate text-brand-navy dark:text-secondary-100" :class="item.read ? 'font-medium' : 'font-bold'">{{ item.title }}</span>
              <span class="block text-xs truncate text-secondary-600 dark:text-secondary-400">{{ item.body }}</span>
              <span class="block text-[11px] text-secondary-400 mt-0.5">{{ relativeTime(item.createdAt) }}</span>
            </span>
            <span v-if="!item.read" class="w-2 h-2 rounded-full bg-kila-mint mt-2 flex-shrink-0"></span>
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { Bell, BellRing, Mail, Link2, AtSign } from 'lucide-vue-next';
import { usePushNotifications, type AppPushNotification } from '@/composables/usePushNotifications';
import { useNotification } from '@/composables/useNotification';

/** Campana del encabezado: avisos de correo nuevo y de asociación a embarque/OC (KILA-362). */
const { t } = useI18n();
const { notifications, unreadCount, permission, isPushEnabled, pushError, start, enablePush, markAllAsRead, open } = usePushNotifications();
const { showNotification } = useNotification();

/** Activar el push del navegador diciendo qué pasó si no se pudo: antes fallaba en silencio. */
async function onEnablePush() {
  const ok = await enablePush();
  if (ok) {
    showNotification('success', t('notifications.pushEnabled.title'), t('notifications.pushEnabled.body'));
    return;
  }
  const reason = pushError.value?.reason || 'token';
  showNotification('error', t('notifications.pushError.title'), t(`notifications.pushError.${reason}`, { detail: pushError.value?.detail || '' }));
}

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});

async function toggle() {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) return;
  const rect = rootRef.value?.getBoundingClientRect();
  if (!rect) return;
  await nextTick();
  const width = panelRef.value?.offsetWidth || 360;
  const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
  panelStyle.value = { top: `${rect.bottom + 6}px`, left: `${left}px` };
}

async function openItem(item: AppPushNotification) {
  isOpen.value = false;
  await open(item);
}

function relativeTime(date: Date) {
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('notifications.time.now');
  if (minutes < 60) return t('notifications.time.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.time.hours', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('notifications.time.days', { count: days });
  return date.toLocaleDateString();
}

function onDocumentClick(event: MouseEvent) {
  const target = event.target as Node;
  if (rootRef.value?.contains(target) || panelRef.value?.contains(target)) return;
  isOpen.value = false;
}

onMounted(() => {
  start();
  document.addEventListener('click', onDocumentClick);
});
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));
</script>
